import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Material {
  id: string;
  title: string;
  category: string;
}

export default function HomeworkPage() {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [studentName, setStudentName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const { data, error } = await supabase
        .from('materials')
        .select('id, title, category')
        .eq('is_active', true)
        .order('title');

      if (error) throw error;
      setMaterials(data || []);
    } catch (error) {
      console.error('獲取教材列表失敗:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!file || !selectedMaterial || !studentName || !studentEmail) {
      alert('請填寫完整資料並選擇檔案');
      return;
    }

    setUploading(true);
    setShowFeedback(false);

    try {
      // 上傳檔案到 Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${studentEmail}.${fileExt}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('homework')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // 獲取檔案 URL
      const { data: urlData } = supabase.storage
        .from('homework')
        .getPublicUrl(fileName);

      // 提交作業並獲取 AI 反饋
      const response = await fetch(
        'https://fnhsumfewqfgpjteloga.supabase.co/functions/v1/submit-homework',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            student_email: studentEmail,
            student_name: studentName,
            material_id: selectedMaterial,
            file_url: urlData.publicUrl,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setFeedback(data.feedback);
        setShowFeedback(true);
        // 重置表單
        setFile(null);
        setSelectedMaterial('');
        setStudentName('');
        setStudentEmail('');
      }
    } catch (error) {
      console.error('上傳失敗:', error);
      alert('上傳失敗，請稍後再試');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img src="https://public.readdy.ai/ai/img_res/46c4b423-33ec-4c59-b54c-70c60984f453.png" alt="Logo" className="h-10 w-10 object-contain" />
              <span className="text-xl font-bold text-gray-900">數學學習平台</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/materials" className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
                教材商店
              </Link>
              <Link to="/admin" className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
                管理後台
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-green-50 via-white to-blue-50">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-6">
          <div className="w-20 h-20 flex items-center justify-center mx-auto bg-green-100 rounded-full">
            <i className="ri-file-upload-line text-4xl text-green-600"></i>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900">
            作業上傳與AI批改
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            上傳您的作業，AI將立即為您提供詳細的批改反饋與學習建議
          </p>
        </div>
      </section>

      {/* Upload Form */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <form onSubmit={handleSubmit} className="bg-white border-2 border-gray-200 rounded-3xl p-8 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  學生姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  placeholder="請輸入您的姓名"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  電子郵件 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={studentEmail}
                  onChange={(e) => setStudentEmail(e.target.value)}
                  placeholder="請輸入您的電子郵件"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                選擇教材 <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedMaterial}
                onChange={(e) => setSelectedMaterial(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm cursor-pointer"
                required
              >
                <option value="">請選擇相關教材</option>
                {materials.map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.title} ({material.category})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                上傳作業檔案 <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-500 transition-colors">
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  className="hidden"
                  id="file-upload"
                  required
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
                    <i className="ri-upload-cloud-line text-3xl text-gray-400"></i>
                  </div>
                  {file ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-900">
                        點擊選擇檔案或拖曳至此
                      </p>
                      <p className="text-xs text-gray-500">
                        支援 PDF, DOC, DOCX, JPG, PNG 格式
                      </p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={uploading}
              className="w-full py-4 bg-green-600 text-white rounded-full text-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <span className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>上傳中...</span>
                </span>
              ) : (
                <span className="flex items-center justify-center space-x-2">
                  <i className="ri-send-plane-fill"></i>
                  <span>提交作業</span>
                </span>
              )}
            </button>
          </form>

          {/* AI Feedback */}
          {showFeedback && (
            <div className="mt-8 bg-gradient-to-br from-blue-50 to-green-50 border-2 border-green-200 rounded-3xl p-8 space-y-6 animate-fadeIn">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 flex items-center justify-center bg-green-600 rounded-full">
                  <i className="ri-robot-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">AI 批改結果</h3>
                  <p className="text-sm text-gray-600">智能分析完成</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6">
                <pre className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed font-sans">
                  {feedback}
                </pre>
              </div>

              <div className="flex items-center justify-between bg-white rounded-2xl p-6">
                <div className="space-y-1">
                  <p className="text-sm text-gray-600">需要更詳細的指導？</p>
                  <p className="font-semibold text-gray-900">預約線上教學課程</p>
                </div>
                <Link
                  to="/"
                  className="px-6 py-3 bg-blue-900 text-white rounded-full font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  立即預約
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">AI批改功能特色</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 text-center space-y-4">
              <div className="w-16 h-16 flex items-center justify-center mx-auto bg-blue-100 rounded-full">
                <i className="ri-flashlight-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">即時反饋</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                上傳後立即獲得詳細的批改結果，不需等待
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 text-center space-y-4">
              <div className="w-16 h-16 flex items-center justify-center mx-auto bg-green-100 rounded-full">
                <i className="ri-bar-chart-box-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">詳細分析</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                提供完整的解題分析與改進建議
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 text-center space-y-4">
              <div className="w-16 h-16 flex items-center justify-center mx-auto bg-orange-100 rounded-full">
                <i className="ri-lightbulb-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">學習建議</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                根據您的表現提供個人化學習建議
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © 2025 數學學習平台. 版權所有
            </div>
            <div>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}
